import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, MapPin, Users } from 'lucide-react';
import apiService from '../../services/api';

interface Country {
  id: number;
  code: string;
  name: string;
  nameTranslations: Record<string, string>;
  supportedLanguages: string[];
  flagEmoji: string;
  population?: number;
  coordinates?: any;
  isActive: boolean;
  riskProfile?: any;
  _count?: {
    countryNewsItems: number;
    articleAnalyses: number;
    countryContexts: number;
    analysisPrompts: number;
  };
}

interface CountryStats {
  countryNewsItems: number;
  articleAnalyses: number;
  countryContexts: number;
  analysisPrompts: number;
  contextTypes: number;
  contextTypesList: string[];
  activePrompts: number;
  inactivePrompts: number;
}

interface NotificationState {
  type: 'success' | 'error' | null;
  message: string;
}

export default function CountriesTab() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [countryStats, setCountryStats] = useState<CountryStats | null>(null);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationState>({ type: null, message: '' });

  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    nameTranslations: Record<string, string>;
    supportedLanguages: string[];
    flagEmoji: string;
    population: string;
    isActive: boolean;
  }>({
    code: '',
    name: '',
    nameTranslations: {},
    supportedLanguages: ['en'],
    flagEmoji: '',
    population: '',
    isActive: true,
  });

  useEffect(() => {
    fetchCountries();
  }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (notification.type) {
      const timer = setTimeout(() => {
        setNotification({ type: null, message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/api/admin/countries?includeInactive=${showInactive}`);
      setCountries(response.data);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load countries' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCountryStats = async (code: string) => {
    try {
      const response = await apiService.get(`/api/admin/countries/${code}/stats`);
      setCountryStats(response.data.stats);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load country stats' });
    }
  };

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
    fetchCountryStats(country.code);
    setEditingCountry(null);
    setIsCreating(false);
  };

  const handleEdit = (country: Country) => {
    setEditingCountry(country);
    setFormData({
      code: country.code,
      name: country.name,
      nameTranslations: country.nameTranslations || {},
      supportedLanguages: country.supportedLanguages || ['en'],
      flagEmoji: country.flagEmoji,
      population: country.population?.toString() || '',
      isActive: country.isActive,
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingCountry(null);
    setSelectedCountry(null);
    setFormData({
      code: '',
      name: '',
      nameTranslations: {},
      supportedLanguages: ['en'],
      flagEmoji: '',
      population: '',
      isActive: true,
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        population: formData.population ? parseInt(formData.population) : undefined,
        nameTranslations: formData.nameTranslations,
      };

      if (isCreating) {
        await apiService.post('/api/admin/countries', payload);
        setNotification({ type: 'success', message: 'Country created successfully' });
      } else if (editingCountry) {
        await apiService.put(`/api/admin/countries/${editingCountry.code}`, payload);
        setNotification({ type: 'success', message: 'Country updated successfully' });
      }

      setIsCreating(false);
      setEditingCountry(null);
      fetchCountries();
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to save country' });
    }
  };

  const handleDelete = async (country: Country) => {
    if (!window.confirm(`Are you sure you want to delete ${country.name}? This action may deactivate the country if it has associated data.`)) {
      return;
    }

    try {
      await apiService.delete(`/api/admin/countries/${country.code}`);
      setNotification({ type: 'success', message: 'Country deleted successfully' });
      fetchCountries();
      if (selectedCountry?.code === country.code) {
        setSelectedCountry(null);
        setCountryStats(null);
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to delete country' });
    }
  };

  const resetForm = () => {
    setIsCreating(false);
    setEditingCountry(null);
  };

  return (
    <div className="p-6">
      {/* Help Section */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Country Management</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
          Manage countries available for threat analysis and monitoring:
        </p>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
          <li>• Add new countries for threat monitoring expansion</li>
          <li>• Edit country information and translations</li>
          <li>• View usage statistics (contexts, prompts, analyses)</li>
          <li>• Activate/deactivate countries (soft delete preserves data)</li>
        </ul>
      </div>

      {/* Notification */}
      {notification.type && (
        <div className={`mb-4 p-4 rounded-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Countries List */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Countries
            </h3>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded"
                />
                Show inactive
              </label>
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Country
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading countries...</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {countries.map((country) => (
                <div
                  key={country.code}
                  className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                    selectedCountry?.code === country.code
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  } ${!country.isActive ? 'opacity-60' : ''}`}
                  onClick={() => handleSelectCountry(country)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{country.flagEmoji}</span>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {country.name}
                        </h4>
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          <span>{country.code}</span>
                          {!country.isActive && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {country._count && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
                          <div>{country._count.articleAnalyses} analyses</div>
                          <div>{country._count.countryContexts} contexts</div>
                        </div>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(country);
                        }}
                        className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(country);
                        }}
                        className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Details Panel */}
        <div>
          {(isCreating || editingCountry) ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isCreating ? 'Add New Country' : 'Edit Country'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country Code (ISO 3166-1 alpha-3)
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="EST"
                      maxLength={3}
                      disabled={!isCreating}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Flag Emoji
                    </label>
                    <input
                      type="text"
                      value={formData.flagEmoji}
                      onChange={(e) => setFormData({ ...formData, flagEmoji: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-2xl"
                      placeholder="🇪🇪"
                      maxLength={4}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Country Name (English)
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Estonia"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Population
                  </label>
                  <input
                    type="number"
                    value={formData.population}
                    onChange={(e) => setFormData({ ...formData, population: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="1326535"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Supported Languages for AI Analysis
                  </label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {formData.supportedLanguages.map((lang) => (
                        <span key={lang} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          {lang.toUpperCase()}
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              supportedLanguages: formData.supportedLanguages.filter(l => l !== lang),
                              nameTranslations: Object.fromEntries(
                                Object.entries(formData.nameTranslations).filter(([key]) => key !== lang)
                              )
                            })}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Add language code (e.g., 'fr', 'de')"
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const newLang = e.currentTarget.value.toLowerCase().trim();
                            if (newLang && !formData.supportedLanguages.includes(newLang)) {
                              setFormData({
                                ...formData,
                                supportedLanguages: [...formData.supportedLanguages, newLang],
                                nameTranslations: {
                                  ...formData.nameTranslations,
                                  [newLang]: ''
                                }
                              });
                              e.currentTarget.value = '';
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const input = e.currentTarget.parentElement?.querySelector('input');
                          if (input) {
                            const newLang = input.value.toLowerCase().trim();
                            if (newLang && !formData.supportedLanguages.includes(newLang)) {
                              setFormData({
                                ...formData,
                                supportedLanguages: [...formData.supportedLanguages, newLang],
                                nameTranslations: {
                                  ...formData.nameTranslations,
                                  [newLang]: ''
                                }
                              });
                              input.value = '';
                            }
                          }
                        }}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Languages that AI will use for analysis. Primary language (first) will be used as default.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name Translations
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {formData.supportedLanguages.map((lang) => (
                      <div key={lang}>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {lang.toUpperCase()}
                        </label>
                        <input
                          type="text"
                          value={formData.nameTranslations[lang] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            nameTranslations: {
                              ...formData.nameTranslations,
                              [lang]: e.target.value
                            }
                          })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder={lang === 'en' ? 'Estonia' : ''}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
                    Active
                  </label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </div>
            </div>
          ) : selectedCountry ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{selectedCountry.flagEmoji}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {selectedCountry.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedCountry.code}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!selectedCountry.isActive && (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                      Inactive
                    </span>
                  )}
                  <button
                    onClick={() => handleEdit(selectedCountry)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                </div>
              </div>

              {/* Country Details */}
              <div className="space-y-4">
                {selectedCountry.population && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700 dark:text-gray-300">
                      Population: {selectedCountry.population.toLocaleString()}
                    </span>
                  </div>
                )}

                {/* Usage Statistics */}
                {countryStats && (
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Usage Statistics</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 rounded p-3">
                        <div className="text-lg font-bold text-blue-900">{countryStats.articleAnalyses}</div>
                        <div className="text-xs text-blue-700">Article Analyses</div>
                      </div>
                      
                      <div className="bg-green-50 rounded p-3">
                        <div className="text-lg font-bold text-green-900">{countryStats.countryContexts}</div>
                        <div className="text-xs text-green-700">Country Contexts</div>
                      </div>
                      
                      <div className="bg-purple-50 rounded p-3">
                        <div className="text-lg font-bold text-purple-900">{countryStats.activePrompts}</div>
                        <div className="text-xs text-purple-700">Active Prompts</div>
                      </div>
                      
                      <div className="bg-orange-50 rounded p-3">
                        <div className="text-lg font-bold text-orange-900">{countryStats.contextTypes}</div>
                        <div className="text-xs text-orange-700">Context Types</div>
                      </div>
                    </div>

                    {countryStats.contextTypesList.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Available Context Types:
                        </h5>
                        <div className="flex flex-wrap gap-1">
                          {countryStats.contextTypesList.map((type) => (
                            <span key={type} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Supported Languages */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Supported Languages for AI Analysis</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCountry.supportedLanguages?.map((lang, index) => (
                      <span key={lang} className={`px-2 py-1 rounded text-sm ${
                        index === 0 
                          ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {lang.toUpperCase()}
                        {index === 0 && (
                          <span className="ml-1 text-xs text-blue-600">(primary)</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Translations */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Name Translations</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedCountry.nameTranslations || {}).map(([lang, name]) => (
                      name && (
                        <div key={lang} className="flex items-center gap-3 text-sm">
                          <span className="w-8 text-gray-500 dark:text-gray-400 uppercase font-mono">
                            {lang}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300">{name}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a country to view details and statistics</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}